import type {
  ClientProposalHosting,
  ClientProposalOptions,
  ClientProposalPageScope,
} from "../lib/clientProposal";

interface Props {
  mode: "editing" | "locked";
  options: ClientProposalOptions;
  onChange?: (options: ClientProposalOptions) => void;
  onCancel: () => void;
  onConfirm?: () => void;
  onEdit?: () => void;
}

function ProposalDocumentIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6.5 3.5h7l4 4v13h-11z" />
      <path d="M13.5 3.5v4h4" />
      <path d="M9.5 12h5M9.5 15.5h5" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="m7 7 10 10M17 7 7 17" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m6.5 12.5 3.4 3.4 7.6-8" />
    </svg>
  );
}

function ChoiceCard({
  checked,
  title,
  description,
  badge,
  onClick,
}: {
  checked: boolean;
  title: string;
  description: string;
  badge?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`clientProposal__choice ${checked ? "is-selected" : ""}`}
      role="radio"
      aria-checked={checked}
      onClick={onClick}
    >
      <span className="clientProposal__choiceTop">
        <span className="clientProposal__radio" aria-hidden="true">
          <span />
        </span>
        <span className="clientProposal__choiceCopy">
          <span className="clientProposal__choiceTitle">{title}</span>
          <span className="clientProposal__choiceDescription">{description}</span>
        </span>
      </span>
      {badge && <span className="clientProposal__badge">{badge}</span>}
    </button>
  );
}

export default function ClientProposalConfigurator({
  mode,
  options,
  onChange,
  onCancel,
  onConfirm,
  onEdit,
}: Props) {
  const setHosting = (hosting: ClientProposalHosting) => {
    onChange?.({ ...options, hosting });
  };
  const setPageScope = (pageScope: ClientProposalPageScope) => {
    onChange?.({ ...options, pageScope });
  };

  if (mode === "locked") {
    const pageSummary =
      options.pageScope === "eight" ? "8 pages total" : "Current pages";
    return (
      <div className="clientProposalLock" aria-label="Client Proposal skill locked in">
        <button
          type="button"
          className="clientProposalLock__main"
          onClick={onEdit}
          aria-label={`Edit Client Proposal: S$${options.hosting} per year, ${pageSummary}`}
        >
          <span className="clientProposalLock__icon"><ProposalDocumentIcon /></span>
          <span className="clientProposalLock__copy">
            <span className="clientProposalLock__title">
              Client Proposal
              <span className="clientProposalLock__check" aria-label="Confirmed"><CheckIcon /></span>
            </span>
            <span className="clientProposalLock__summary">
              S${options.hosting}/year <span aria-hidden="true">·</span> {pageSummary}
            </span>
          </span>
          <span className="clientProposalLock__edit">Edit</span>
        </button>
        <button
          type="button"
          className="clientProposalLock__remove"
          onClick={onCancel}
          aria-label="Remove Client Proposal skill"
          title="Remove Client Proposal"
        >
          <CloseIcon />
        </button>
      </div>
    );
  }

  return (
    <section className="clientProposal" aria-labelledby="client-proposal-title">
      <header className="clientProposal__header">
        <span className="clientProposal__icon"><ProposalDocumentIcon /></span>
        <span className="clientProposal__heading">
          <span className="clientProposal__eyebrow">Skill setup</span>
          <strong id="client-proposal-title">Client Proposal</strong>
          <span>Confirm two details</span>
        </span>
        <span className="clientProposal__actions">
          <button
            type="button"
            className="clientProposal__action clientProposal__action--cancel"
            onClick={onCancel}
            aria-label="Cancel Client Proposal"
            title="Cancel"
          >
            <CloseIcon />
          </button>
          <button
            type="button"
            className="clientProposal__action clientProposal__action--confirm"
            onClick={onConfirm}
            aria-label="Confirm and lock Client Proposal"
            title="Confirm"
          >
            <CheckIcon />
          </button>
        </span>
      </header>

      <div className="clientProposal__body">
        <fieldset className="clientProposal__group">
          <legend>
            <span>Annual hosting renewal</span>
            <small>First year is complimentary</small>
          </legend>
          <div className="clientProposal__choices" role="radiogroup" aria-label="Annual hosting renewal">
            <ChoiceCard
              checked={options.hosting === 120}
              title="S$120/year"
              description="Only if quoted to the client"
              onClick={() => setHosting(120)}
            />
            <ChoiceCard
              checked={options.hosting === 240}
              title="S$240/year"
              description="No price mentioned? Use this"
              badge="Default"
              onClick={() => setHosting(240)}
            />
          </div>
        </fieldset>

        <fieldset className="clientProposal__group">
          <legend>
            <span>Page scope</span>
            <small>Choose what the client was offered</small>
          </legend>
          <div className="clientProposal__choices" role="radiogroup" aria-label="Page scope">
            <ChoiceCard
              checked={options.pageScope === "current"}
              title="Current pages"
              description="Use the pages already agreed"
              badge="Default"
              onClick={() => setPageScope("current")}
            />
            <ChoiceCard
              checked={options.pageScope === "eight"}
              title="8 pages total"
              description="Only if eight pages were offered"
              onClick={() => setPageScope("eight")}
            />
          </div>
        </fieldset>

        <p className="clientProposal__footnote">
          Tap the tick to lock this in. Nothing is sent until you tap Send.
        </p>
      </div>
    </section>
  );
}
